/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState } from 'react'
import dayjs from 'dayjs'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { ENTITY_TYPES, ENTITY_TYPE_INFO } from '@sportninja/common/sagas/utils'
import { getFeedPostDate } from '@sportninja/common/utils/utils'
import { t } from '@sportninja/common/i18n'
import req from '@sportninja/common/api/request'
import { isCanlan } from '@sportninja/common/utils/customer-name'

import Picture from '../../components/Picture'
import { Flex } from '../../components/Layout'
import LoadingSpinner from '../../components/LoadingSpinner'
import FormSheet from '../../components/Form/FormSheet'
import { feedItemForm } from '../../components/Form/form-configs'
import DeleteModal from '../../components/Modal/DeleteModal'
import sportninjaLogo from './sportninja-post.png'
import canlanLogo from './canlan-post.png'
import {
  paragraphLarge,
  paragraphMedium,
  paragraphXSmall,
  weightBold,
  weightRegular,
} from 'src/components/css'
import Text from 'src/components/ts-components/Text'
import {
  HeartOutlined,
  HeartFilled,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  SafetyCertificateFilled,
} from '@ant-design/icons'
import { Dropdown } from 'antd'
import { Image } from 'antd'

const convertMarkdownToHtml = (markdown) => {
  if (!markdown) {
    return ''
  }

  // Helper function to escape HTML special characters
  const escapeHtml = (text) => {
    const htmlEntities = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }
    return text?.replace(/[&<>"']/g, (char) => htmlEntities[char])
  }

  // Convert headers
  markdown = markdown?.replace(
    /^(#{1,6})\s(.+)$/gm,
    (match, hashes, content) => {
      const level = hashes.length
      return `<h${level}>${escapeHtml(content)}</h${level}>`
    }
  )

  // Convert a single * to bold
  markdown = markdown.replace(/\*(.*?)\*/g, '<strong>$1</strong>')
  markdown = markdown.replace(/<strong>/g, '<span style="font-weight: bold;">')
  markdown = markdown.replace(/<\/strong>/g, '</span>')

  // Convert to italic
  markdown = markdown.replace(/\b_(.*?)_\b/g, '<em>$1</em>')
  markdown = markdown.replace(/<em>/g, '<span style="font-style: italic;">')
  markdown = markdown.replace(/<\/em>/g, '</span>')

  // Convert to strikethrough considering the ~
  markdown = markdown.replace(/~(.*?)~/g, '<s>$1</s>')
  markdown = markdown.replace(
    /<s>/g,
    '<span style="text-decoration: line-through;">'
  )
  markdown = markdown.replace(/<\/s>/g, '</span>')

  markdown = markdown.replace(/```(.*?)```/g, '<pre><code>$1</code></pre>')
  markdown = markdown.replace(
    /<pre>/g,
    '<span style="font-family: monospace;">'
  )
  markdown = markdown.replace(/<\/pre>/g, '</span>')

  // Convert links
  markdown = markdown.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

  // Convert unordered lists
  markdown = markdown.replace(/^\s*[-*+]\s(.+)$/gm, '<li>$1</li>')
  markdown = markdown.replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')

  // Convert ordered lists
  markdown = markdown.replace(/^\s*\d+\.\s(.+)$/gm, '<li>$1</li>')
  markdown = markdown.replace(/(<li>.*<\/li>)/s, '<ol>$1</ol>')

  // Convert code blocks
  markdown = markdown.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')

  // Convert inline code
  markdown = markdown.replace(/`([^`]+)`/g, '<code>$1</code>')

  // Convert paragraphs (must be done last)
  markdown = markdown.replace(/^(?!<[uo]l|<li|<h\d|<pre)(.+)$/gm, '<p>$1</p>')

  return markdown
}

const HomePost = ({
  canEdit,
  canDelete,
  post,
  isMobile,
  refreshFeed,
  entityType,
  entityId,
  isInFrame,
}) => {
  const {
    id,
    body,
    created_at,
    imagePath,
    imageWidth,
    imageHeight,
    is_liked,
    parent,
    parent_id,
    parent_type,
    url,
    title,
  } = post

  const htmlContent = convertMarkdownToHtml(body)

  const [isLiked, setIsLiked] = useState(is_liked)
  const [likeInProgress, setLikeInProgress] = useState(false)
  const [likeCount, setLikeCount] = useState(post?.like_count)

  const onLikePressed = async () => {
    setLikeInProgress(true)
    try {
      if (isLiked) {
        await req(`/social-feed/${id}/like`, { method: 'DELETE' })
        setIsLiked(false)
        if (likeCount > 0) {
          setLikeCount(likeCount - 1)
        }
      } else {
        await req(`/social-feed/${id}/like`, { method: 'POST' })
        setIsLiked(true)
        if (likeCount >= 0) {
          setLikeCount(likeCount + 1)
        }
      }
    } finally {
      setLikeInProgress(false)
    }
  }

  let iconName, to
  switch (parent_type) {
    case 'organization':
      iconName = 'users'
      to = ENTITY_TYPE_INFO[ENTITY_TYPES.org].linkRoot
      break
    case 'schedule':
      iconName = 'calendar-alt'
      to = ENTITY_TYPE_INFO[ENTITY_TYPES.schedule].linkRoot
      break
    case 'team':
      iconName = 'user-friends'
      to = ENTITY_TYPE_INFO[ENTITY_TYPES.team].linkRoot
      break
  }

  let formattedHost
  if (url) {
    const parsedUrl = url ? new URL(url) : url
    const host = parsedUrl?.host
    formattedHost = host?.replace(/^www\./, '')
  }

  const isAppPost = parent === null

  // Determine what we need to scale the image by
  const scaleFactor = imageWidth / (isMobile ? window.innerWidth - 48 : 766)
  // New height is the minimum of its original height or the scaled height
  const height = Math.min(
    500,
    Math.round(Math.min(imageHeight, imageHeight / scaleFactor))
  )

  const items = [
    {
      key: '1',
      label: (
        <FormSheet
          Button={({ onClick }) => (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 8px;
              `}
              onClick={onClick}
            >
              <EditOutlined
                css={css`
                  font-size: 16px;
                  color: ${colors.PRIMARY};
                `}
              />
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
              >
                Edit
              </Text>
            </div>
          )}
          form={feedItemForm(post, false)}
          label={t('Web:editPost')}
          title={t('Web:editPost')}
          onSubmit={async ({ imageData, ...form }) => {
            const body = JSON.stringify(form)
            await req(`/${entityType}/${entityId}/social-feed/${id}`, {
              method: 'PUT',
              body,
            })
            if (imageData) {
              await req(`/social-feed/${id}/image`, {
                method: 'POST',
                body: imageData,
              })
            }
            await refreshFeed()
          }}
        />
      ),
    },
    {
      key: '2',
      label: (
        <DeleteModal
          title='Delete Post'
          message='Are you sure you want to delete this post? This cannot be undone.'
          onDelete={async () => {
            await req(`/${entityType}/${entityId}/social-feed/${id}`, {
              method: 'DELETE',
            })
            await refreshFeed()
          }}
          Button={({ ...props }) => (
            <div
              {...props}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 8px;
              `}
            >
              <DeleteOutlined
                css={css`
                  font-size: 16px;
                  color: ${colors.PRIMARY};
                `}
              />
              <Text
                variant='paragraphSmall'
                weight='regular'
                color={colors.WHITE}
              >
                Delete
              </Text>
            </div>
          )}
        />
      ),
    },
  ]

  return (
    <article
      className='home-post'
      css={css`
        position: relative;

        margin-bottom: 16px;
        padding: 16px;
        background: #27303e;
        border: 1px solid ${colors.SOFT_STEEL};
        border-radius: 8px;
      `}
    >
      {/* Logo, parent name, and post date */}
      <Flex
        noFlex
        alignItems='center'
        justifyContent='space-between'
        css={css`
          margin-bottom: 16px;
        `}
      >
        <a
          css={css`
            display: flex;
            align-items: center;
            ${to && 'cursor: pointer;'}
          `}
          href={to ? `${to}/${parent_id}` : undefined}
          target={to ? '_blank' : undefined}
          rel='noreferrer'
        >
          {isAppPost ? (
            <img
              src={isCanlan ? canlanLogo : sportninjaLogo}
              css={css`
                border-radius: ${isCanlan && '4px;'};
                height: 40px;
                width: 40px;
              `}
            />
          ) : (
            <Picture
              square
              fit
              size='kxsmall'
              imageId={getImageThumbnailId(parent)}
              iconName={iconName}
            />
          )}
          <div
            css={css`
              margin-left: 14px;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 4px;
              `}
            >
              <div
                css={css`
                  ${paragraphMedium}
                  ${weightBold}
                `}
              >
                {parent?.name || t('common:appName')}
              </div>
              {isAppPost ? (
                <SafetyCertificateFilled
                  css={css`
                    font-size: 16px;
                    color: ${colors.INFO_300};
                  `}
                />
              ) : null}
            </div>
            <div
              css={css`
                ${paragraphXSmall}
                color: ${colors.NEUTRAL_50};
              `}
              title={dayjs(created_at).format('h:mm A - MMMM D, YYYY')}
            >
              {getFeedPostDate(created_at)}
            </div>
          </div>
        </a>
        {canEdit && entityType && entityId && (
          <Dropdown
            menu={{ items }}
            placement='bottomRight'
            trigger={['click']}
            key={'dropdown'}
          >
            <MoreOutlined
              css={css`
                font-size: 24px;
                color: ${colors.PRIMARY};
                cursor: pointer;
              `}
            />
          </Dropdown>
        )}
      </Flex>

      {/* Title */}
      <h2
        css={css`
          ${paragraphLarge}
          ${weightBold}
          color: ${colors.NEUTRAL_0};
          margin-bottom: 16px;
        `}
      >
        {title}
      </h2>

      {/* Body */}
      <div
        dangerouslySetInnerHTML={{ __html: htmlContent }}
        className='home-post-body'
        css={css`
          ${paragraphMedium}
          ${weightRegular}
          color: ${colors.NEUTRAL_30};
          white-space: pre-wrap;
        `}
      />

      {/* Image */}
      {imagePath && (
        <div
          css={css`
            width: 100%;
            height: 100%;
            border-radius: 8px;
            margin-top: 16px;
            justify-content: center;
            align-items: center;
            display: flex;
            background-color: ${colors.SECONDARY_400};
            overflow: hidden;
            .ant-image {
              width: 100% !important;
              height: 100% !important;
              border-radius: 8px !important;
            }
            .ant-image .ant-image-img {
              height: 100% !important;
              object-fit: cover !important;
            }
          `}
        >
          <Image
            src={imagePath}
            preview={{
              mask: <div>Click to Zoom</div>,
              maskStyle: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                backdropFilter: 'blur(2px)',
              },
            }}
            draggable={false}
          />
        </div>
      )}

      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          align-self: stretch;
          margin-top: 16px;
        `}
      >
        {!isInFrame ? (
          <div
            css={css`
              display: flex;
              padding: 8px 16px;
              align-items: center;
              gap: 4px;
              border-radius: 8px;
              background: ${colors.SECONDARY_400};
            `}
          >
            {likeInProgress ? (
              <div
                css={css`
                  height: 16px;
                  width: 16px;
                  position: relative;
                `}
              >
                <LoadingSpinner size={1} borderSize={2} />
              </div>
            ) : (
              <button
                onClick={likeInProgress ? undefined : onLikePressed}
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  gap: 4px;
                `}
              >
                {isLiked ? (
                  <HeartFilled
                    css={css`
                      font-size: 16px;
                      color: ${colors.PRIMARY};
                    `}
                  />
                ) : (
                  <HeartOutlined
                    css={css`
                      font-size: 16px;
                      color: ${colors.PRIMARY};
                    `}
                  />
                )}
                {likeCount > 0 ? (
                  <span
                    css={css`
                      ${paragraphXSmall}
                      ${weightRegular}
                      color: ${colors.NEUTRAL_0};
                    `}
                  >
                    {likeCount} {likeCount === 1 ? 'Like' : 'Likes'}
                  </span>
                ) : null}
              </button>
            )}
          </div>
        ) : null}
        {formattedHost ? (
          <a
            css={css`
              display: flex;
              padding: 8px 16px;
              align-items: center;
              gap: 4px;
              border-radius: 8px;
              border: 1px solid ${colors.PRIMARY};
              @media (max-width: 768px) {
                max-width: 50%;
              }
            `}
            href={url}
            target='_blank'
            rel='noopener noreferrer'
          >
            <Text
              variant='paragraphXSmall'
              weight='bold'
              color={colors.PRIMARY}
              style={css`
                @media (max-width: 768px) {
                  white-space: nowrap;
                  overflow: hidden;
                  text-overflow: ellipsis;
                }
              `}
            >
              Go To:
            </Text>
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.PRIMARY}
              style={css`
                @media (max-width: 768px) {
                  max-width: 80px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                }
              `}
            >
              {formattedHost}
            </Text>
          </a>
        ) : null}
      </div>
    </article>
  )
}

export default HomePost
